import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RequireCustomer from '../auth/RequireCustomer';
import { useCustomer } from '../../contexts/CustomerContext';
import {
  CustomerOrderSummary,
  listMyOrders,
} from '../../api/CustomerClient';
import { PointsLedgerEntry } from '../../api/PointsClient';

const AddressBlock: React.FC<{ title: string; a: any }> = ({ title, a }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
      {title}
    </Typography>
    {a ? (
      <Box sx={{ fontSize: 14, lineHeight: 1.5 }}>
        {(a.firstName || a.lastName) && (
          <div>{[a.firstName, a.lastName].filter(Boolean).join(' ')}</div>
        )}
        {a.street && <div>{a.street}</div>}
        {a.additional && <div>{a.additional}</div>}
        {(a.city || a.province || a.postal) && (
          <div>
            {[a.city, a.province, a.postal].filter(Boolean).join(', ')}
          </div>
        )}
        {a.phone && <div>{a.phone}</div>}
      </Box>
    ) : (
      <Typography variant="body2" sx={{ color: 'text.disabled' }}>
        Not set — add one at checkout.
      </Typography>
    )}
  </Box>
);

const formatTotal = (s: string) => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : s;
};

// Points are stored as integer cents-of-value; format both ways so the
// customer can see "250 pts ($2.50)" without doing the math.
const formatPointsAsDollars = (pts: number) => `$${(pts / 100).toFixed(2)}`;

const ledgerLabel = (e: PointsLedgerEntry): string => {
  switch (e.reason) {
    case 'purchase':
      return 'Order earned';
    case 'friend_code':
      return 'Friend referral';
    case 'redemption':
      return 'Redeemed';
    case 'refund_reversal':
      return 'Refund adjustment';
    case 'adjustment':
      return 'Adjustment';
    default:
      return e.reason;
  }
};

const ledgerPendingLabel = (e: PointsLedgerEntry): string | null => {
  if (e.redeemable_at === null) return 'pending';
  const ts = Date.parse(e.redeemable_at);
  if (Number.isFinite(ts) && ts > Date.now()) {
    return `available ${formatDate(e.redeemable_at)}`;
  }
  return null;
};

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const AccountInner: React.FC = () => {
  const {
    customer,
    customerLoading,
    customerError,
    points,
    friendCode,
    idToken,
    signOutCustomer,
  } = useCustomer();
  const [orders, setOrders] = useState<CustomerOrderSummary[] | null>(null);
  const [ordersErr, setOrdersErr] = useState<string | null>(null);
  const [copySnack, setCopySnack] = useState(false);

  const copyFriendCode = async () => {
    if (!friendCode?.code) return;
    try {
      await navigator.clipboard.writeText(friendCode.code);
      setCopySnack(true);
    } catch (err) {
      console.warn('[account] clipboard write failed:', err);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const t = await idToken();
      if (!t) return;
      try {
        const res = await listMyOrders(t, { limit: 25 });
        if (!cancelled) setOrders(res.items);
      } catch (err: any) {
        const msg = err?.response?.data ?? err?.message ?? String(err);
        if (!cancelled) setOrdersErr(typeof msg === 'string' ? msg : 'Failed to load orders');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  if (customerLoading && !customer) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (customerError) {
    return (
      <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Couldn't load your profile</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{customerError}</Typography>
      </Box>
    );
  }

  if (!customer) return null;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>My account</Typography>
        <Button variant="outlined" size="small" onClick={signOutCustomer} sx={{ textTransform: 'none' }}>
          Sign out
        </Button>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Profile</Typography>
          <Stack spacing={1}>
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Email</Typography>
              <Typography variant="body1">{customer.email ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Display name</Typography>
              <Typography variant="body1">{customer.display_name ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Phone</Typography>
              <Typography variant="body1">{customer.phone ?? '—'}</Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
            <Typography variant="h6">Points</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              1 point = 1¢ off a future order
            </Typography>
          </Stack>
          {points === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <>
              <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    Available
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {points.available.toLocaleString()} pts
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {formatPointsAsDollars(points.available)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    Pending
                  </Typography>
                  <Typography variant="h5">
                    {points.pending.toLocaleString()} pts
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {formatPointsAsDollars(points.pending)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    Lifetime earned
                  </Typography>
                  <Typography variant="h5" sx={{ color: 'text.secondary' }}>
                    {points.lifetime_earned.toLocaleString()} pts
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled' }}>
                Pending points become available a while after your order is fulfilled.
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Friend code</Typography>
          {friendCode === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '1.4rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    px: 2,
                    py: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  {friendCode.code}
                </Box>
                <Tooltip title="Copy code">
                  <IconButton onClick={copyFriendCode} size="small">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Share your code with friends. When they enter it at checkout
                on their first order, you'll earn points on that purchase.
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {points && points.recent.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1.5 }}>Recent points activity</Typography>
            <Stack divider={<Divider flexItem />} spacing={1}>
              {points.recent.map((e) => {
                const pendingLabel = ledgerPendingLabel(e);
                const positive = e.delta >= 0;
                return (
                  <Stack
                    key={e.id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ py: 1 }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {ledgerLabel(e)}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {formatDate(e.created_at)}
                        </Typography>
                        {pendingLabel && (
                          <Chip
                            size="small"
                            label={pendingLabel}
                            sx={{ height: 18, fontSize: '0.7rem' }}
                          />
                        )}
                      </Stack>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: positive ? 'success.main' : 'text.primary',
                      }}
                    >
                      {positive ? '+' : ''}
                      {e.delta.toLocaleString()} pts
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Saved addresses</Typography>
          <Stack spacing={2}>
            <AddressBlock title="Default delivery" a={customer.default_delivery_address} />
            <Divider flexItem />
            <AddressBlock title="Default billing" a={customer.default_billing_address} />
          </Stack>
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.disabled' }}>
            Your first order after signing in saves your addresses here automatically.
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Recent orders</Typography>
          {ordersErr && (
            <Typography variant="body2" sx={{ color: 'error.main', mb: 1 }}>
              {ordersErr}
            </Typography>
          )}
          {orders === null && !ordersErr && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {orders && orders.length === 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No orders yet.
            </Typography>
          )}
          {orders && orders.length > 0 && (
            <Stack divider={<Divider flexItem />} spacing={1}>
              {orders.map((o) => (
                <Stack
                  key={o.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 1 }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      <Link to={`/order/${o.id}`} style={{ color: 'inherit' }}>
                        {formatDate(o.created_at)}
                      </Link>
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {o.status}
                    </Typography>
                  </Box>
                  <Typography variant="body2">{formatTotal(o.total)}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={copySnack}
        autoHideDuration={2000}
        onClose={() => setCopySnack(false)}
        message="Friend code copied"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

const AccountPage: React.FC = () => (
  <RequireCustomer>
    <AccountInner />
  </RequireCustomer>
);

export default AccountPage;
